import { FC, ReactNode } from 'react';

import { InputTelephone } from '@eneco/sparky';
import { isValidPhoneNumber as isValidInternationalPhoneNumber } from 'libphonenumber-js';
import { useFormContext, useFormState, FieldErrors, Path } from 'react-hook-form';
import * as yup from 'yup';

type FormValues = {
  [key: string]: unknown;
};

// Helper function to get nested error by path (e.g., "personalDetailsStep.phoneNumber")
const getNestedError = (errors: FieldErrors<FormValues>, path: string): string | undefined => {
  const parts = path.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: any = errors;

  for (const part of parts) {
    if (current?.[part] === undefined) {
      return undefined;
    }
    current = current[part];
  }

  return current?.message;
};

const PhoneNumberInputField: FC<{
  name: string;
  hint: ReactNode;
  label: string;
  placeholder: string;
  defaultValue?: string;
  isOptional?: boolean;
  isDisabled?: boolean;
}> = ({ name, ...props }) => {
  const { register } = useFormContext();
  const { errors } = useFormState<FormValues>();

  return (
    <InputTelephone
      autoComplete="tel"
      error={getNestedError(errors, name)}
      {...props}
      {...register(name as Path<FormValues>)}
    />
  );
};

export const phoneValidationScheme = ({
  validationMessage,
  requiredMessage,
  isRequired = false,
}: {
  validationMessage: string;
  requiredMessage?: string;
  isRequired?: boolean;
}) => {
  let schema = yup.string();

  if (isRequired) {
    schema = schema.required(requiredMessage);
  }

  schema = schema.test('phoneNumberValid', validationMessage, value => {
    if (!value || value.trim() === '') {
      return true;
    }
    return isValidPhoneNumber(value);
  });

  return schema;
};

function isValidPhoneNumber(phoneNumber: string): boolean {
  // Without a default country, libphonenumber-js only accepts international numbers (E.164, starting with +)
  return isValidInternationalPhoneNumber(phoneNumber);
}

export default PhoneNumberInputField;
